import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { customerService, washService } from '../../services/firebase'
import { QrCode, Star, Gift, Calendar, Car, ChevronRight, CreditCard } from 'lucide-react'
import { Link } from 'react-router-dom'
import LoadingSpinner from '../../components/LoadingSpinner'
import BottomNav from '../../components/BottomNav'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import { voucherProgramService } from '@/services/voucherProgramService'
import { formatRand, getVoucherLoyalty } from '@/utils/voucherLoyalty'

export default function CustomerDashboard() {
  const { currentUser } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [activeWash, setActiveWash] = useState(null)
  const [recentWashes, setRecentWashes] = useState([])
  const [washStats, setWashStats] = useState({ total: 0, thisMonth: 0 })
  const [voucherProgram, setVoucherProgram] = useState(null)
  const [voucherSummary, setVoucherSummary] = useState({ spendProgress: 0, balance: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchCustomerData() {
      if (!currentUser) return
      try {
        await customerService.ensureCustomerProfileForUser(currentUser)
        const customerData = await customerService.getCustomerByUserId(currentUser.uid)
        setCustomer(customerData)

        const program = await voucherProgramService.get()
        setVoucherProgram(program)

        const ids = await customerService.listCustomerIdsForUser(currentUser)
        const profiles = await customerService.getCustomersByIds(ids)
        const spendProgress = profiles.reduce((sum, c) => sum + Number(c?.voucherSpendProgress || 0), 0)
        const balance = profiles.reduce((sum, c) => sum + Number(c?.voucherBalance || 0), 0)
        setVoucherSummary({ spendProgress, balance })
        if (ids.length > 0) {
          const washes = await washService.getCustomerWashesByCustomerIds(ids)
          const active = (washes || []).find((w) =>
            ['waiting', 'washing', 'drying', 'polishing', 'interior', 'ready'].includes(w.status)
          )
          setActiveWash(active || null)
          setRecentWashes((washes || []).slice(0, 3))

          const now = new Date()
          const isThisMonth = (w) => {
            const d = w?.createdAt?.toDate?.() ? w.createdAt.toDate() : new Date(w?.createdAt || 0)
            if (Number.isNaN(d.getTime())) return false
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
          }
          setWashStats({
            total: (washes || []).length,
            thisMonth: (washes || []).filter(isThisMonth).length,
          })
        } else {
          setActiveWash(null)
          setRecentWashes([])
          setWashStats({ total: 0, thisMonth: 0 })
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchCustomerData()
  }, [currentUser])

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-background">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  const emailMismatch =
    !!customer?.email &&
    !!currentUser?.email &&
    String(customer.email).toLowerCase() !== String(currentUser.email).toLowerCase()

  const placeholderPhone = String(customer?.phone || '').trim() === '+27821234567'
  const hasName = Boolean(String(customer?.name || '').trim())
  const hasPhone = Boolean(String(customer?.phone || '').trim()) && !placeholderPhone

  const needsClientDetails = !customer || !hasName || !hasPhone || emailMismatch
  const needsVehicleOnboarding = customer?.isSystemProfile === true

  if (needsClientDetails || needsVehicleOnboarding) {
    return (
      <>
        <div className="min-h-screen bg-background pb-32">
          <div className="mx-auto max-w-lg space-y-6 px-4 py-8">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Customer
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {needsClientDetails ? 'Complete your profile' : 'Add your vehicle'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {needsClientDetails
                ? 'Add your name and mobile number so staff can check you in quickly.'
                : 'Add the car you usually bring in. Staff will pre-fill it at the wash bay.'}
            </p>

            <Link
              to="/customer/profile"
              className={cn(buttonVariants({ className: 'w-full' }))}
            >
              Go to profile
            </Link>
          </div>
        </div>
        <BottomNav />
      </>
    )
  }

  const firstName = customer?.name?.split(' ')[0] || 'there'
  const loyalty = getVoucherLoyalty(
    {
      voucherSpendProgress: voucherSummary.spendProgress,
      voucherBalance: voucherSummary.balance,
    },
    voucherProgram
  )

  const stats = [
    {
      label: 'Total washes',
      value: washStats.total,
      icon: Car,
    },
    {
      label: 'Voucher balance',
      value: formatRand(loyalty.balance),
      icon: Gift,
    },
    {
      label: 'Towards next voucher',
      value: formatRand(loyalty.progress),
      icon: Star,
    },
    {
      label: 'This month',
      value: washStats.thisMonth,
      icon: Calendar,
    },
  ]

  return (
    <div className="min-h-screen bg-background pb-32">
      <div className="mx-auto max-w-4xl space-y-8 px-4 py-8">
        <div className="space-y-2 border-b border-border pb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Customer
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            Hello, {firstName}
          </h1>
          <p className="text-sm text-muted-foreground max-w-lg">
            Your wash summary, QR check-in, and loyalty progress in one place.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <Card key={s.label} className="shadow-sm border-border/80">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 md:p-6">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight md:text-xs">
                  {s.label}
                </CardTitle>
                <div className="rounded-lg bg-primary/10 p-2 text-primary md:rounded-md md:p-1.5">
                  <s.icon className="size-5 md:size-3.5" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 md:px-6 md:pb-6">
                <p className="text-3xl font-semibold tabular-nums tracking-tight md:text-2xl">{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="shadow-sm border-border/80">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-lg md:text-base">Your current wash</CardTitle>
            <CardDescription className="text-base md:text-sm">
              Track where your car is in the wash process.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 md:px-6 md:pb-6">
            {!activeWash ? (
              <p className="text-base text-muted-foreground md:text-sm">No active wash right now.</p>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-semibold text-foreground truncate md:text-sm">
                    {activeWash?.carDetails?.make || 'Vehicle'} {activeWash?.carDetails?.model || ''}
                    {activeWash?.carDetails?.licensePlate ? ` · ${activeWash.carDetails.licensePlate}` : ''}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 md:text-xs">
                    Service: {activeWash.serviceType || '—'}
                  </p>
                </div>
                <div className="shrink-0 rounded-full border border-border px-3 py-1.5 text-sm font-semibold md:py-1 md:text-xs">
                  {String(activeWash.status || '').replace('_', ' ').toUpperCase()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <Link to="/customer/qr" className="group block">
            <Card className="h-full border-border/80 shadow-sm transition-all hover:border-primary/30 hover:shadow-md">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary md:size-12 md:rounded-lg">
                  <QrCode className="size-7 md:size-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-foreground md:text-sm">My QR code</p>
                  <p className="text-base text-muted-foreground md:text-sm">Show staff for quick check-in</p>
                </div>
                <ChevronRight className="size-6 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 md:size-5" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/customer/history" className="group block">
            <Card className="h-full border-border/80 shadow-sm transition-all hover:border-primary/30 hover:shadow-md">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground group-hover:text-foreground md:size-12 md:rounded-lg">
                  <Car className="size-7 md:size-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-foreground md:text-sm">Wash history</p>
                  <p className="text-base text-muted-foreground md:text-sm">Past visits and services</p>
                </div>
                <ChevronRight className="size-6 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 md:size-5" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/customer/subscriptions" className="group block sm:col-span-2">
            <Card className="h-full border-border/80 shadow-sm transition-all hover:border-primary/30 hover:shadow-md">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground group-hover:text-foreground md:size-12 md:rounded-lg">
                  <CreditCard className="size-7 md:size-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-foreground md:text-sm">Subscription packages</p>
                  <p className="text-base text-muted-foreground md:text-sm">Choose a package and show a checkout code</p>
                </div>
                <ChevronRight className="size-6 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 md:size-5" />
              </CardContent>
            </Card>
          </Link>
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-lg">Voucher progress</CardTitle>
                <CardDescription>
                  Spend {formatRand(loyalty.threshold)} to earn a {formatRand(loyalty.rewardAmount)} voucher.
                </CardDescription>
              </div>
              <Link
                to="/customer/profile"
                className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'w-fit')}
              >
                View profile
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress to next voucher</span>
              <span className="font-medium tabular-nums text-foreground">
                {loyalty.progressLabel}
              </span>
            </div>
            <Progress value={loyalty.pct} className="h-2" />
            <p className="text-xs text-muted-foreground">
              Vouchers are issued automatically when you reach the spend threshold.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Recent washes</CardTitle>
            <Link
              to="/customer/history"
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'sm' }),
                'text-muted-foreground'
              )}
            >
              View all
            </Link>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            {recentWashes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-3 rounded-full bg-muted p-4 text-muted-foreground">
                  <Car className="size-8" />
                </div>
                <p className="text-sm font-medium text-foreground">No washes yet</p>
                <p className="mt-1 text-xs text-muted-foreground max-w-xs">
                  After your first visit, recent washes will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentWashes.map((w) => {
                  const d = w?.createdAt?.toDate?.() ? w.createdAt.toDate() : new Date(w?.createdAt || 0)
                  const when = Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
                  return (
                    <div key={w.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{w.serviceType || 'Wash'}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {when}
                          {w?.carDetails?.licensePlate ? ` · ${w.carDetails.licensePlate}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums text-foreground">
                          R{Number(w.amount || 0).toFixed(2)}
                        </p>
                        <p className="text-[11px] text-muted-foreground capitalize">{String(w.status || '').replace('_', ' ')}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BottomNav />
    </div>
  )
}
